import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type ReactElement,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { CommerceActionNotification } from '@/components/toast/commerce-action-notification';
import { GiftCardRemovedNotification } from '@/components/toast/gift-card-removed-notification';
import {
    AddedToCartToastContent,
    BasketQuotaExceededToastContent,
    FailedToAddToCartToastContent,
    FailedToUpdateCartToastContent,
    MinOrderFeedbackToastContent,
    RemovedFromCartToastContent,
    type AddedToCartToastOptions,
    type MinOrderFeedbackToastOptions,
    type RecipeAddedToCartToastOptions,
    type RemovedFromCartToastOptions,
    type WishlistAddToCartToastOptions,
} from '@/components/toast/commerce-feedback-content';
import {
    commerceFeedbackContentClassName,
    type CommerceFeedbackPresentation,
} from '@/components/toast/commerce-notification-styles';

import { useTranslation } from 'react-i18next';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { COMMERCE_FEEDBACK_TOASTER_ID } from '@/components/toast/commerce-feedback-toaster';
import { CommerceNotificationDrawer } from '@/components/toast/commerce-notification-drawer';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import {
    CommerceFeedbackContext,
    type GiftCardRemovedFeedbackOptions,
    type WishlistSavedFeedbackOptions,
} from '@/providers/commerce-feedback-context';

const COMMERCE_FEEDBACK_DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

const COMMERCE_TOAST_DURATION_MS = 4000;

type DesktopWishlistNotification = WishlistSavedFeedbackOptions & {
    id: string;
};

type DesktopGiftCardNotification = GiftCardRemovedFeedbackOptions & {
    id: string;
};

function DesktopWishlistNotificationPortal({
    notification,
    onDismiss,
}: {
    notification: DesktopWishlistNotification;
    onDismiss: () => void;
}) {
    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div
            className="pointer-events-none fixed top-[calc(var(--header-height,0px)+0.75rem)] right-4 z-[100] md:right-6"
            aria-hidden={false}>
            <DesktopWishlistNotificationPortalInner notification={notification} onDismiss={onDismiss} />
        </div>,
        document.body
    );
}

function DesktopWishlistNotificationPortalInner({
    notification,
    onDismiss,
}: {
    notification: DesktopWishlistNotification;
    onDismiss: () => void;
}) {
    return (
        <div className="pointer-events-auto">
            <CommerceActionNotification
                variant="desktop"
                productName={notification.productName}
                listNames={notification.listNames}
                removedListNames={notification.removedListNames}
                alreadyInList={notification.alreadyInList}
                bulkAddSummary={notification.bulkAddSummary}
                onDismiss={onDismiss}
            />
        </div>
    );
}

function DesktopGiftCardNotificationPortal({
    notification,
    onDismiss,
}: {
    notification: DesktopGiftCardNotification;
    onDismiss: () => void;
}) {
    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div
            className="pointer-events-none fixed top-[calc(var(--header-height,0px)+0.75rem)] right-4 z-[100] md:right-6"
            aria-hidden={false}>
            <div className="pointer-events-auto">
                <GiftCardRemovedNotification
                    variant="desktop"
                    maskedLabel={notification.maskedLabel}
                    onDismiss={onDismiss}
                />
            </div>
        </div>,
        document.body
    );
}

export function CommerceFeedbackProvider({ children }: PropsWithChildren): ReactElement {
    const { t } = useTranslation('product');
    const { t: tAccount } = useTranslation('account');
    const { t: tCart, i18n } = useTranslation('cart');
    const { currency } = useSite();
    const isDesktop = useMediaQuery(COMMERCE_FEEDBACK_DESKTOP_MEDIA_QUERY);
    const [desktopWishlistNotification, setDesktopWishlistNotification] = useState<DesktopWishlistNotification | null>(
        null
    );
    const [desktopGiftCardNotification, setDesktopGiftCardNotification] = useState<DesktopGiftCardNotification | null>(
        null
    );
    const [mobileFeedbackContent, setMobileFeedbackContent] = useState<ReactNode | null>(null);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const giftCardDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const presentation: CommerceFeedbackPresentation = isDesktop ? 'toast' : 'drawer';

    const clearDismissTimer = useCallback(() => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
    }, []);

    const clearGiftCardDismissTimer = useCallback(() => {
        if (giftCardDismissTimerRef.current) {
            clearTimeout(giftCardDismissTimerRef.current);
            giftCardDismissTimerRef.current = null;
        }
    }, []);

    const dismissDesktopWishlistNotification = useCallback(() => {
        clearDismissTimer();
        setDesktopWishlistNotification(null);
    }, [clearDismissTimer]);

    const dismissDesktopGiftCardNotification = useCallback(() => {
        clearGiftCardDismissTimer();
        setDesktopGiftCardNotification(null);
    }, [clearGiftCardDismissTimer]);

    useEffect(() => {
        return () => {
            clearDismissTimer();
            clearGiftCardDismissTimer();
        };
    }, [clearDismissTimer, clearGiftCardDismissTimer]);

    const showCommerceFeedback = useCallback(
        (content: ReactNode) => {
            if (isDesktop) {
                toast(content, {
                    toasterId: COMMERCE_FEEDBACK_TOASTER_ID,
                    duration: COMMERCE_TOAST_DURATION_MS,
                    closeButton: true,
                });
                return;
            }

            setMobileFeedbackContent(content);
        },
        [isDesktop]
    );

    const showWishlistSaved = useCallback(
        (options: WishlistSavedFeedbackOptions) => {
            if (options.listNames.length === 0 && (options.removedListNames?.length ?? 0) === 0) {
                return;
            }

            clearDismissTimer();
            const id = crypto.randomUUID();
            setDesktopWishlistNotification({ id, ...options });
            dismissTimerRef.current = setTimeout(() => {
                setDesktopWishlistNotification((current) => (current?.id === id ? null : current));
            }, COMMERCE_TOAST_DURATION_MS);
        },
        [clearDismissTimer]
    );

    const showGiftCardRemoved = useCallback(
        (options: GiftCardRemovedFeedbackOptions) => {
            if (isDesktop) {
                clearGiftCardDismissTimer();
                const id = crypto.randomUUID();
                setDesktopGiftCardNotification({ id, ...options });
                giftCardDismissTimerRef.current = setTimeout(() => {
                    setDesktopGiftCardNotification((current) => (current?.id === id ? null : current));
                }, COMMERCE_TOAST_DURATION_MS);
                return;
            }

            setMobileFeedbackContent(
                <GiftCardRemovedNotification variant="mobile-drawer" maskedLabel={options.maskedLabel} />
            );
        },
        [clearGiftCardDismissTimer, isDesktop]
    );

    const showAddedToCart = useCallback(
        (options: AddedToCartToastOptions) => {
            showCommerceFeedback(
                <AddedToCartToastContent
                    {...options}
                    presentation={presentation}
                    t={t}
                    tAccount={tAccount}
                    tCart={tCart}
                    currency={currency}
                    language={i18n.language}
                />
            );
        },
        [currency, i18n.language, presentation, showCommerceFeedback, t, tAccount, tCart]
    );

    const showWishlistAddToCart = useCallback(
        (options: WishlistAddToCartToastOptions) => {
            showAddedToCart({
                messageKey: 'addedAllToCartForWishlist',
                addedItemCount: options.addedItemCount,
                unavailableItemCount: options.unavailableItemCount,
                approachingPromotions: options.approachingPromotions,
                editorialPromotions: options.editorialPromotions,
                isBasketQuotaExceeded: options.isBasketQuotaExceeded,
            });
        },
        [showAddedToCart]
    );
    const showRecipeAddedToCart = useCallback(
        (options: RecipeAddedToCartToastOptions) => {
            showAddedToCart({
                messageKey: 'addedAllToCartForRecipe',
                addedItemCount: options.addedItemCount,
                unavailableItemCount: options.unavailableItemCount,
                approachingPromotions: options.approachingPromotions,
                editorialPromotions: options.editorialPromotions,
                isBasketQuotaExceeded: options.isBasketQuotaExceeded,
            });
        },
        [showAddedToCart]
    );

    const showRemovedFromCart = useCallback(
        (options: RemovedFromCartToastOptions) => {
            showCommerceFeedback(<RemovedFromCartToastContent {...options} presentation={presentation} t={t} />);
        },
        [presentation, showCommerceFeedback, t]
    );

    const showFailedToAddCart = useCallback(() => {
        showCommerceFeedback(<FailedToAddToCartToastContent presentation={presentation} t={t} />);
    }, [presentation, showCommerceFeedback, t]);

    const showFailedToUpdateCart = useCallback(() => {
        showCommerceFeedback(<FailedToUpdateCartToastContent presentation={presentation} t={t} />);
    }, [presentation, showCommerceFeedback, t]);

    const showBasketQuotaExceeded = useCallback(() => {
        showCommerceFeedback(<BasketQuotaExceededToastContent presentation={presentation} tAccount={tAccount} />);
    }, [presentation, showCommerceFeedback, tAccount]);

    const showMinOrderFeedback = useCallback(
        (options: MinOrderFeedbackToastOptions) => {
            showCommerceFeedback(<MinOrderFeedbackToastContent {...options} presentation={presentation} t={tCart} />);
        },
        [presentation, showCommerceFeedback, tCart]
    );

    const showGeneralAlert = useCallback(
        (message: string) => {
            showCommerceFeedback(
                <p className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
                    {message}
                </p>
            );
        },
        [presentation, showCommerceFeedback]
    );

    const showCartMessage = useCallback(
        (message: string) => {
            showCommerceFeedback(
                <p className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
                    {message}
                </p>
            );
        },
        [presentation, showCommerceFeedback]
    );

    const value = useMemo(
        () => ({
            showWishlistSaved,
            showGiftCardRemoved,
            showAddedToCart,
            showRemovedFromCart,
            showFailedToAddCart,
            showFailedToUpdateCart,
            showBasketQuotaExceeded,
            showMinOrderFeedback,
            showWishlistAddToCart,
            showRecipeAddedToCart,
            showCartMessage,
            showGeneralAlert,
        }),
        [
            showWishlistSaved,
            showGiftCardRemoved,
            showAddedToCart,
            showRemovedFromCart,
            showFailedToAddCart,
            showFailedToUpdateCart,
            showBasketQuotaExceeded,
            showMinOrderFeedback,
            showWishlistAddToCart,
            showRecipeAddedToCart,
            showCartMessage,
            showGeneralAlert,
        ]
    );

    return (
        <CommerceFeedbackContext.Provider value={value}>
            {children}
            {desktopWishlistNotification ? (
                <DesktopWishlistNotificationPortal
                    notification={desktopWishlistNotification}
                    onDismiss={dismissDesktopWishlistNotification}
                />
            ) : null}
            {desktopGiftCardNotification ? (
                <DesktopGiftCardNotificationPortal
                    notification={desktopGiftCardNotification}
                    onDismiss={dismissDesktopGiftCardNotification}
                />
            ) : null}
            <CommerceNotificationDrawer
                open={mobileFeedbackContent !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setMobileFeedbackContent(null);
                    }
                }}
                closeAriaLabel={t('wishlistSheet.close')}>
                {mobileFeedbackContent}
            </CommerceNotificationDrawer>
        </CommerceFeedbackContext.Provider>
    );
}
